import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, 
  Loader2,
  FileText,
  Calendar,
  ChevronRight,
  Filter,
  Pill
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { 
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata'
  });
}

export default function PrescriptionsListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create modal
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState('');

  const fetchPrescriptions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/prescriptions`, { withCredentials: true });
      setPrescriptions(data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to fetch prescriptions');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPatients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patients?is_active=true`, { withCredentials: true });
      setPatients(data);
    } catch (error) {
      console.error('Failed to fetch patients', error);
    }
  }, []);

  useEffect(() => {
    fetchPrescriptions();
    fetchPatients();
  }, [fetchPrescriptions, fetchPatients]);

  const handleCreatePrescription = async (e) => {
    e.preventDefault();
    if (!selectedPatientId) {
      toast.error('Please select a patient');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        patient_id: selectedPatientId,
        morning_routine: { medicines: [], notes: '' },
        evening_routine: { medicines: [], notes: '' },
        oral_medicines: { medicines: [], notes: '' },
        procedures: '',
        precautions: '',
        follow_up_date: null,
        media_links: []
      };
      const { data } = await axios.post(`${API_URL}/api/prescriptions`, payload, { withCredentials: true });
      toast.success('Prescription created');
      setIsCreateOpen(false);
      setSelectedPatientId('');
      navigate(`/prescriptions/${data.id}/edit`);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to create prescription');
    } finally {
      setIsSubmitting(false);
    }
  };

  const countMedicines = (prescription) => {
    return (
      (prescription.morning_routine?.medicines?.length || 0) +
      (prescription.evening_routine?.medicines?.length || 0) +
      (prescription.oral_medicines?.medicines?.length || 0)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="prescriptions-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="prescriptions-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight text-foreground">
            Prescriptions
          </h1>
          <p className="text-muted-foreground mt-1">
            Create and manage patient prescriptions
          </p>
        </div>
        <Button 
          onClick={() => setIsCreateOpen(true)}
          className="bg-primary hover:bg-primary/90 rounded-full"
          data-testid="create-prescription-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Prescription
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{prescriptions.length}</p>
              <p className="text-xs text-muted-foreground">Total Prescriptions</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success/10">
              <Pill className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">
                {prescriptions.reduce((acc, p) => acc + countMedicines(p), 0)}
              </p>
              <p className="text-xs text-muted-foreground">Total Medicines</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-info/10">
              <Calendar className="w-5 h-5 text-info" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">
                {prescriptions.filter(p => p.follow_up_date).length}
              </p>
              <p className="text-xs text-muted-foreground">With Follow-ups</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Prescriptions List */}
      <div className="space-y-4">
        {prescriptions.length === 0 ? (
          <Card className="border-border shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">
                No prescriptions found. Create your first prescription.
              </p>
            </CardContent>
          </Card>
        ) : (
          prescriptions.map((prescription) => (
            <Card 
              key={prescription.id} 
              className="border-border shadow-sm card-interactive cursor-pointer"
              onClick={() => navigate(`/prescriptions/${prescription.id}/edit`)}
              data-testid={`prescription-card-${prescription.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-medium">{prescription.patient_name}</h3>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(prescription.created_at)}</span>
                        <span>•</span>
                        <span>{countMedicines(prescription)} medicines</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {prescription.follow_up_date && (
                      <Badge variant="outline" className="bg-info/10 text-info border-info/20">
                        Follow-up: {formatDate(prescription.follow_up_date)}
                      </Badge>
                    )}
                    {prescription.media_links?.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {prescription.media_links.length} media
                      </Badge>
                    )}
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Prescription Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="create-prescription-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">New Prescription</DialogTitle>
            <DialogDescription>Select a patient to create a new prescription</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreatePrescription} className="space-y-4">
            <div className="space-y-2">
              <Select value={selectedPatientId} onValueChange={setSelectedPatientId}>
                <SelectTrigger className="rounded-xl" data-testid="select-patient-for-prescription">
                  <SelectValue placeholder="Select a patient" />
                </SelectTrigger>
                <SelectContent>
                  {patients.map((patient) => (
                    <SelectItem key={patient.id} value={patient.id}>
                      {patient.name} ({patient.phone})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)} className="rounded-full">
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting || !selectedPatientId} 
                className="bg-primary hover:bg-primary/90 rounded-full" 
                data-testid="create-prescription-submit"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create & Edit
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
